// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./css/dashboard.css";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { API_BASE, request } from "../services/server";
import useKoncernMembers from "../hooks/useKoncernMembers";
import useKoncernScope from "../hooks/useKoncernScope";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import KoncernScopePicker from "./components/KoncernScopePicker";
import {
  WorkspaceStatusBadge,
  WorkspaceCloseBanner,
  ActivateWorkspaceButton,
  CloseWorkspaceButton,
} from "./components/WorkspaceLifecycleUI";
import ActivityFeed from "./components/ActivityFeed";

/**
 * Dashboard
 * - Loads dashboard fast (no AI blocking)
 * - Loads value drivers separately (AI / slow)
 * - Shows a nice inline loader while value drivers load
 */
export default function Dashboard() {
  const { company } = useParams(); // orgnr in your router

  // Koncern scope — when active we swap which dashboard endpoint to hit.
  const { koncernInfo, isKoncern } = useKoncernMembers(company);
  const { scope, setScope, activeOrgnr, isAllMode } = useKoncernScope(company);
  const dashOrgnr = activeOrgnr || company;

  const [data, setData] = useState(null);
  const [koncernData, setKoncernData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [nextAction, setNextAction] = useState(null);

  // Fetch "Nästa steg"-card payload — separate from main dashboard fetch
  // so the card can show even if value drivers / koncern data fails.
  useEffect(() => {
    if (!dashOrgnr || isAllMode) return;
    const ctrl = new AbortController();
    fetch(`${API_BASE}/company/dashboard/${dashOrgnr}/next-action/`, {
      signal: ctrl.signal,
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) setNextAction(j); })
      .catch(() => {});
    return () => ctrl.abort();
  }, [dashOrgnr, isAllMode]);

  // Value drivers state (separate call)
  const [drivers, setDrivers] = useState({
    loading: true,
    error: "",
    payload: null,
    refreshed: false,
    coverage: null,
    quality: null,
  });

  const DASHBOARD_URL = `${API_BASE}/company/dashboard/${dashOrgnr}/`;
  const KONCERN_DASHBOARD_URL = `${API_BASE}/company/koncern-dashboard/${company}/`;
  const VALUE_DRIVERS_URL = `${API_BASE}/company/value-drivers/${dashOrgnr}/`;

  // ---- Fetch dashboard fast (cache + refresh) ----
  useEffect(() => {
    if (!company) return;

    const ctrl = new AbortController();

    // "Hela koncernen" mode — hit the aggregated endpoint.
    if (isAllMode) {
      setLoading(true);
      (async () => {
        try {
          const res = await fetch(KONCERN_DASHBOARD_URL, {
            signal: ctrl.signal,
            credentials: "include",
            headers: { Accept: "application/json" },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const fresh = await res.json();
          setKoncernData(fresh);
          setData(null);
          setErr("");
        } catch (e) {
          if (e?.name === "AbortError") return;
          console.error(e);
          setErr("Kunde inte ladda koncerndata.");
        } finally {
          setLoading(false);
        }
      })();
      return () => ctrl.abort();
    }

    setKoncernData(null);

    const cacheKey = `company:${dashOrgnr}:dashboard:v2`;

    // 1) Read cache for instant render
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setData(parsed);
        setLoading(false);
      } catch {
        // ignore
      }
    }

    // 2) Fetch fresh
    (async () => {
      try {
        const res = await fetch(DASHBOARD_URL, {
          signal: ctrl.signal,
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const fresh = await res.json();
        setData(fresh);
        setErr("");
        sessionStorage.setItem(cacheKey, JSON.stringify(fresh));

        // Soft URL upgrade: when the user landed via /dashboard/<orgnr>/
        // and the backend returned a slug, rewrite the URL in-place to
        // /dashboard/<slug>/. No reload, no React reroute — just shifts
        // bookmarks and back-button history toward the new key.
        try {
          const slug = fresh?.workspace?.slug;
          if (
            slug &&
            company &&
            company !== slug &&
            /^\d{10}$/.test(String(company).replace(/-/g, ""))
          ) {
            const newPath = window.location.pathname.replace(
              new RegExp(`/${company}(?=$|/)`),
              `/${slug}`,
            );
            if (newPath !== window.location.pathname) {
              window.history.replaceState(
                null,
                "",
                newPath + window.location.search + window.location.hash,
              );
            }
          }
        } catch {
          // If anything goes wrong, leave the URL alone — purely cosmetic.
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error(e);
        if (!data) setErr("Kunde inte ladda bolagsdata.");
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, dashOrgnr, isAllMode]);

  // ---- Fetch value drivers separately (parallel, non-blocking) ----
  // DEMO: disabled — value drivers card is hidden so we skip the LLM call.
  // Re-enable by removing the `if (true)` guard below.
  useEffect(() => {
    if (!company) return;
    if (true) return; // DEMO: skip value drivers fetch

    let cancelled = false;
    const ctrl = new AbortController();

    // reset drivers state on company change
    setDrivers({
      loading: true,
      error: "",
      payload: null,
      refreshed: false,
      coverage: null,
      quality: null,
    });

    (async () => {
      try {
        const res = await fetch(VALUE_DRIVERS_URL, {
          signal: ctrl.signal,
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const out = await res.json();
        if (cancelled) return;

        setDrivers({
          loading: false,
          error: "",
          payload: out?.payload ?? null,
          refreshed: !!out?.refreshed,
          coverage: out?.coverage ?? null,
          quality: out?.quality ?? null,
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error(e);
        if (cancelled) return;
        setDrivers({
          loading: false,
          error: "Kunde inte ladda värdedrivare just nu.",
          payload: null,
          refreshed: false,
          coverage: null,
          quality: null,
        });
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [company]); // VALUE_DRIVERS_URL derived from company

  const org = data?.organisation;
  const financials = data?.financials;
  const valuation = data?.valuation;
  const riskProfile = data?.risk_profile;
  const ownerQ = data?.owner_questionnaire;
  const completeness = data?.completeness || {};

  // Koncern
  const [koncernValuation, setKoncernValuation] = useState(null);

  useEffect(() => {
    if (!isKoncern || !company) return;
    request(`/valuation/workspace/${company}/koncern/`)
      .then((d) => setKoncernValuation(d))
      .catch(() => {});
  }, [isKoncern, company]);

  // Workspace lifecycle metadata — drives the close-countdown banner,
  // status badge, and (when pending) the silent activate button.
  const workspaceMeta = data?.workspace || null;
  const isLockedState = workspaceMeta && (
    workspaceMeta.deal_status === "pending"
    || workspaceMeta.deal_status === "closing"
    || workspaceMeta.deal_status === "closed"
  );

  return (
    <Main navItems={makeCompanyNav(company)}>
      <div className="dashboard-root">
        {/* Close-countdown banner — visible only when deal_status=closing */}
        <WorkspaceCloseBanner workspace={workspaceMeta} />

        {/* Status pill + activate-button (when pending) — at the top so
            the broker sees lifecycle state before scanning numbers. */}
        {workspaceMeta && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: isLockedState ? "1px solid #FCD34D" : "1px solid transparent",
          }}>
            <WorkspaceStatusBadge status={workspaceMeta.deal_status} />
            {workspaceMeta.deal_status === "pending" && (
              <>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  Workspace är låst tills det aktiveras.
                </span>
                <div style={{ marginLeft: "auto" }}>
                  <ActivateWorkspaceButton
                    workspace={workspaceMeta}
                    onActivated={() => window.location.reload()}
                  />
                </div>
              </>
            )}
            {workspaceMeta.payment_status === "past_due" && (
              <span style={{ fontSize: 12, color: "#991B1B", marginLeft: "auto" }}>
                ⚠ Senaste betalningen misslyckades — uppdatera kortet.
              </span>
            )}
          </div>
        )}

        {/* "Nästa steg"-card — one prominent CTA based on stage + completion */}
        {nextAction?.next_action && !isAllMode && (
          <NextActionCard data={nextAction} />
        )}

        {/* Scope picker — visible on koncern workspaces only */}
        {isKoncern && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <KoncernScopePicker koncernInfo={koncernInfo} scope={scope} setScope={setScope} />
          </div>
        )}

        {/* "Hela koncernen" — aggregated totals card */}
        {isAllMode && koncernData?.totals && (
          <div style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12,
            padding: "16px 20px", marginBottom: 20,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A8A", marginBottom: 8 }}>
              Koncernen totalt ({koncernData.totals.companies_count} bolag)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>Omsättning</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(koncernData.totals.revenue || 0)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>EBITDA</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(koncernData.totals.ebitda || 0)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>Nettoskuld</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(koncernData.totals.net_debt || 0)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== KONCERN OVERVIEW ========== */}
        {isKoncern && koncernInfo && !isAllMode && (
          <div style={{
            background: "#F5F5F4", border: "1px solid #E7E5E4", borderRadius: 12,
            padding: "16px 20px", marginBottom: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <svg width="18" height="18" fill="none" stroke="#57534E" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/>
              </svg>
              <strong style={{ fontSize: 14, color: "#1C1917" }}>Koncern — {koncernInfo.all.length} bolag</strong>
              {koncernValuation?.consolidated?.value_mid != null && (
                <span style={{ marginLeft: "auto", fontSize: 16, fontWeight: 700, color: "#57534E" }}>
                  {new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(koncernValuation.consolidated.value_mid)}
                </span>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(koncernInfo.all.length, 4)}, 1fr)`, gap: 8 }}>
              {koncernInfo.all.map((c) => {
                const v = (koncernValuation?.individual || []).find((i) => i.orgnr === c.orgnr);
                return (
                  <div key={c.orgnr} style={{
                    padding: "10px 14px", background: "#fff", borderRadius: 8,
                    border: c.orgnr === company ? "2px solid #57534E" : "1px solid #e5e7eb",
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.orgnr}</div>
                    {v?.valuation?.value_mid != null && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginTop: 4 }}>
                        {new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(v.valuation.value_mid)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ========== HEADER ========== */}
        <header className="dashboard-header">
          <div>
            <h1 className="dashboard-title">{org?.name || "Bolagsdashboard"}</h1>
            <div className="dashboard-meta-row">
              <span className="dashboard-meta">
                {org?.orgnr || org?.organisationnummer || company || "—"}
              </span>
              {org?.juridisk_form && (
                <span className="dashboard-pill">{org.juridisk_form}</span>
              )}
              {org?.status && (
                <span
                  className={`dashboard-status-pill ${
                    org.status === "Aktiv"
                      ? "dashboard-status-pill--ok"
                      : "dashboard-status-pill--warn"
                  }`}
                >
                  {org.status}
                </span>
              )}
            </div>
            {(org?.postort || org?.land || org?.sni_text) && (
              <div className="dashboard-meta-row dashboard-meta-row--secondary">
                {org?.postort && <span>{org.postort}</span>}
                {org?.land && <span>• {org.land}</span>}
                {org?.sni_text && <span>• {org.sni_text}</span>}
              </div>
            )}
          </div>

          {/* Completeness chips */}
          <div className="dashboard-completeness">
            <CompletenessChip
              label="Finansiella data"
              active={completeness.has_financials}
            />
            <CompletenessChip label="Värdering" active={completeness.has_valuation} />
            <CompletenessChip
              label="Riskprofil"
              active={completeness.has_risk_profile}
            />
            <CompletenessChip label="Deal terms" active={completeness.has_deal_terms} />
            <CompletenessChip
              label="Redo för prisförslag"
              active={completeness.ready_for_suggested_price}
              highlight
            />
          </div>
        </header>

        {/* ========== LOADING / ERROR ========== */}
        {loading && (
          <div className="dashboard-state">
            <div className="dashboard-spinner" />
            <span>Laddar bolag…</span>
          </div>
        )}

        {err && !loading && (
          <div className="dashboard-state dashboard-state--error" role="alert">
            {err}
          </div>
        )}

        {/* ========== MAIN LAYOUT ========== */}
        {!loading && !err && data && (
          <>
            {/* FULL WIDTH: M&A timeline */}
            <section className="dashboard-section-full">
              <TimelineCard
                companyId={company}
                completeness={completeness}
                ownerQ={ownerQ}
                valuation={valuation}
                risk={riskProfile}
                coverage={drivers.coverage}
              />
            </section>

            {/* ROW 1 */}
            {/* DEMO: ValueLeversCard hidden, ValuationCard goes full width.
                Restore the dashboard-section-grid + ValueLeversCard block when re-enabling. */}
            <section className="dashboard-section-full">
              <ValuationCard valuation={valuation} completeness={completeness} />
            </section>
            {false && (
              <section className="dashboard-section-grid">
                <ValueLeversCard
                  valuation={valuation}
                  risk={riskProfile}
                  ownerQ={ownerQ}
                  aiPayload={drivers.payload}
                  aiLoading={drivers.loading}
                  aiError={drivers.error}
                  aiRefreshed={drivers.refreshed}
                  quality={drivers.quality}
                  coverage={drivers.coverage}
                />
              </section>
            )}

            {/* ROW 2 */}
            <section className="dashboard-section-full">
              <FinancialOverviewCard rawYears={financials?.years || []} />
            </section>

            {/* LAST */}
            <section className="dashboard-section-full">
              <IMChecklistCard completeness={completeness} orgnr={dashOrgnr} />
            </section>

            {/* Activity feed — senaste händelser i workspacet */}
            <section className="dashboard-section-full">
              <ActivityFeed workspaceKey={company} variant="widget" limit={20} title="Senaste aktivitet" />
            </section>
          </>
        )}
      </div>

      {/* Minimal extra CSS for the value drivers loader + precision panel */}
      <style>{`
        .dashboard-ai-loading {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: 1px solid rgba(148,163,184,0.35);
          background: rgba(248,250,252,0.7);
          border-radius: 12px;
          margin-top: 8px;
        }
        .dashboard-ai-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: currentColor;
          opacity: 0.6;
          animation: dashboardAiPulse 1.2s ease-in-out infinite;
        }
        .dashboard-ai-dot:nth-child(2){ animation-delay: 0.15s; opacity: 0.45; }
        .dashboard-ai-dot:nth-child(3){ animation-delay: 0.30s; opacity: 0.30; }
        @keyframes dashboardAiPulse {
          0%, 100% { transform: translateY(0); opacity: 0.25; }
          50% { transform: translateY(-4px); opacity: 0.9; }
        }
        .dashboard-ai-loading-text {
          font-size: 12px;
          color: #475569;
          line-height: 1.2;
        }
        .dashboard-ai-loading-sub {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }
        .dashboard-ai-error {
          margin-top: 8px;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(239,68,68,0.30);
          background: rgba(254,242,242,0.7);
          color: #991b1b;
          font-size: 12px;
        }
        .dashboard-evidence {
          margin-top: 4px;
          font-size: 11px;
          color: #64748b;
          line-height: 1.2;
        }

        /* Precision panel */
        .dashboard-precision {
          margin-top: 10px;
          border: 1px solid rgba(148,163,184,0.35);
          background: rgba(248,250,252,0.8);
          border-radius: 14px;
          padding: 12px 12px;
        }
        .dashboard-precision-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .dashboard-precision-title {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
        }
        .dashboard-precision-sub {
          margin-top: 2px;
          font-size: 12px;
          color: #475569;
          line-height: 1.2;
        }
        .dashboard-precision-meter {
          width: 120px;
          height: 10px;
          position: relative;
          border-radius: 999px;
          overflow: hidden;
          flex: 0 0 auto;
        }
        .dashboard-precision-meter-bg {
          position: absolute;
          inset: 0;
          background: rgba(148,163,184,0.25);
        }
        .dashboard-precision-meter-fill {
          position: absolute;
          inset: 0;
          width: 0%;
          background: rgba(15,23,42,0.85);
        }
        .dashboard-precision-next {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px dashed rgba(148,163,184,0.35);
        }
        .dashboard-precision-next-title {
          font-size: 12px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 8px;
        }
        .dashboard-precision-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .dashboard-precision-tag {
          font-size: 11px;
          color: #0f172a;
          background: rgba(226,232,240,0.9);
          border: 1px solid rgba(148,163,184,0.35);
          padding: 6px 10px;
          border-radius: 999px;
          line-height: 1;
        }
        .dashboard-precision-note {
          margin-top: 8px;
          font-size: 11px;
          color: #64748b;
          line-height: 1.25;
        }
      `}</style>
    </Main>
  );
}

/* ======================= Small helpers ======================= */

function CompletenessChip({ label, active, highlight = false }) {
  const base = "dashboard-chip";
  const cls = [base, active && `${base}--on`, highlight && `${base}--highlight`]
    .filter(Boolean)
    .join(" ");
  return <span className={cls}>{label}</span>;
}

function formatCurrency(n, currency = "SEK") {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return (
    new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)) +
    ` ${currency}`
  );
}

/* ======================= Precision panel ======================= */

function PrecisionPanel({ quality, coverage }) {
  // Graceful fallback if API hasn't updated yet
  const q = quality && typeof quality === "object" ? quality : null;

  const level = q?.level || "low";
  const done = q?.progress?.done ?? null;
  const total = q?.progress?.total ?? null;
  const missing = Array.isArray(q?.missing) ? q.missing : [];

  const pct =
    done != null && total != null && total > 0 ? Math.round((done / total) * 100) : null;

  const levelLabel =
    level === "high" ? "Hög precision" : level === "medium" ? "Medel precision" : "Lägre precision";

  const levelHint =
    level === "high"
      ? "Analysen är som mest tillförlitlig."
      : level === "medium"
      ? "Bra grund – du kan göra den mer prissäker."
      : "Bygger främst på det som finns i siffrorna just nu.";

  // If we don't have quality, try to still show something minimal using coverage
  const fallbackMissing = () => {
    if (!coverage || typeof coverage !== "object") return [];
    const candidates = [
      { key: "has_5_annual_reports", label: "5 bokslut (årsredovisningar)", ok: !!coverage.has_5_annual_reports },
      { key: "has_ltm_12m", label: "SIE / senaste 12 månader (LTM)", ok: !!coverage.has_ltm_12m },
      { key: "has_owner_questionnaire", label: "Ägarformulär", ok: !!coverage.has_owner_questionnaire },
      { key: "has_risk_profile", label: "Riskprofil", ok: !!coverage.has_risk_profile },
      { key: "has_ddq", label: "Due diligence-checklista", ok: !!coverage.has_ddq },
    ];
    return candidates.filter((x) => !x.ok).map((x) => ({ key: x.key, label: x.label }));
  };

  const missingToShow = missing.length ? missing : fallbackMissing();

  return (
    <div className={`dashboard-precision dashboard-precision--${level}`}>
      <div className="dashboard-precision-top">
        <div>
          <div className="dashboard-precision-title">{levelLabel}</div>
          <div className="dashboard-precision-sub">
            {pct != null ? `${pct}% klart (${done}/${total}) • ` : ""}{levelHint}
          </div>
        </div>

        {pct != null && (
          <div className="dashboard-precision-meter" aria-label="Precision progress">
            <div className="dashboard-precision-meter-bg" />
            <div
              className="dashboard-precision-meter-fill"
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
          </div>
        )}
      </div>

      {missingToShow.length > 0 && (
        <div className="dashboard-precision-next">
          <div className="dashboard-precision-next-title">För mer prissäker analys, lägg till:</div>
          <div className="dashboard-precision-tags">
            {missingToShow.slice(0, 6).map((m) => (
              <span key={m.key || m.label} className="dashboard-precision-tag">
                {m.label || m.key}
              </span>
            ))}
          </div>
          <div className="dashboard-precision-note">
            När du fyller i dessa steg uppdateras drivarna automatiskt och blir mer “köpar-ready”.
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================= M&A Timeline ======================= */

function TimelineCard({ companyId, completeness, ownerQ, valuation, risk, coverage }) {
  // Hämta lifecycle separat så vi kan visa "Avsluta projekt"-knappen
  // när alla timeline-steg är klara. Hooken är cachad per slug så ingen
  // dubblerings-cost även om Dashboard har annan lifecycle-konsument.
  const { workspace: wsLifecycle, reload: reloadLifecycle } = useWorkspaceLifecycle(companyId);
  const hasBokslut = !!(coverage?.has_5_annual_reports);
  const hasSie = !!(coverage?.has_ltm_12m);
  const storageKey = companyId ? `company:${companyId}:ma_timeline` : null;

  const [manualDone, setManualDone] = React.useState({});
  const [activeStepId, setActiveStepId] = React.useState(null);
  const [showRequirements, setShowRequirements] = React.useState(true);

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") setManualDone(parsed);
    } catch {}
  }, [storageKey]);

  const steps = [
    {
      id: "bokslut",
      title: "Ladda upp bokslut",
      autoDone: hasBokslut || !!completeness?.has_financials,
      details: [
        "Ladda upp samtliga bokslut (3–5 år bakåt) i systemet.",
        "Säkerställ att räkenskapsår och bolagsuppgifter stämmer.",
      ],
    },
    {
      id: "sie",
      title: "Ladda upp SIE-filer",
      autoDone: hasSie || !!completeness?.has_sie,
      details: [
        "Importera SIE-filer för löpande bokföring.",
        "Kontrollera att perioderna täcker önskad LTM/YTD.",
      ],
    },
    {
      id: "valuation",
      title: "Beräkna värdering och prisintervall",
      autoDone: !!valuation && !!completeness?.has_valuation,
      details: [
        "Kör värderingsmotorn på uppdaterade finansiella data.",
        "Säkerställ rimlighet i multiplar och antaganden.",
        "Ta fram preliminärt prisintervall och diskussionsunderlag.",
      ],
    },
    {
      id: "questionnaire",
      title: "Fyll i ägarformulär och riskprofil",
      autoDone: false,
      details: [
        "Besvara frågor om ägarbild, säljmotiv och dealstruktur.",
        "Fyll i riskprofil (kundkoncentration, nyckelpersoner m.m.).",
        "Identifiera kända juridiska och operativa risker.",
      ],
    },
    {
      id: "im_dataroom",
      title: "Ta fram IM och datarum",
      autoDone: false,
      details: [
        "Skapa ett första utkast till IM baserat på data och ägarformulär.",
        "Samla avtal, policys och övriga underlag i ett datarum.",
        "Säkerställ att materialet är konsekvent och uppdaterat.",
      ],
    },
    {
      id: "dd_negotiation",
      title: "Due diligence och förhandling",
      autoDone: false,
      details: [
        "Genomför strukturerad due diligence med potentiella köpare.",
        "Hantera Q&A och kompletterande material i datarum.",
        "Förhandla huvudsakliga deal terms (pris, villkor, garantier).",
      ],
    },
    {
      id: "sold",
      title: "Sålt",
      autoDone: false,
      details: [
        "Aktie- eller inkråmsöverlåtelseavtal är signerat.",
        "Tillträde genomfört enligt överenskommen tidplan.",
        "Köpeskilling och eventuella earn-outs hanterade.",
      ],
    },
  ];

  const stepsWithDone = steps.map((s, idx) => ({
    ...s,
    stepNumber: idx + 1,
    done: s.autoDone || !!manualDone[s.id],
  }));

  const total = stepsWithDone.length;
  const doneCount = stepsWithDone.filter((s) => s.done).length;

  const firstIncompleteIndex = stepsWithDone.findIndex((s) => !s.done);
  const lastReachedIndex =
    firstIncompleteIndex === -1 ? stepsWithDone.length - 1 : firstIncompleteIndex - 1;

  const progressRatio =
    total <= 1 ? 0 : Math.max(0, Math.min(1, lastReachedIndex / (stepsWithDone.length - 1)));
  const progressPct = Math.round(progressRatio * 100);

  const currentIndex =
    firstIncompleteIndex === -1 ? stepsWithDone.length - 1 : firstIncompleteIndex;

  const signature = stepsWithDone.map((s) => `${s.id}:${s.done ? 1 : 0}`).join("|");

  React.useEffect(() => {
    if (!stepsWithDone.length) return;
    const firstIncomplete =
      stepsWithDone.find((s) => !s.done) || stepsWithDone[stepsWithDone.length - 1];
    setActiveStepId((prev) => prev ?? firstIncomplete.id);
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeStep = stepsWithDone.find((s) => s.id === activeStepId) || stepsWithDone[0];

  const handleToggleStep = (step) => {
    if (!storageKey || step.autoDone) return;
    setManualDone((prev) => {
      const next = { ...prev, [step.id]: !prev[step.id] };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const statusForStep = (step, index) => {
    if (step.done) return { label: "Klar", className: "dashboard-timeline-status--done" };
    if (index === currentIndex)
      return { label: "Pågående", className: "dashboard-timeline-status--current" };
    return { label: "Kommande", className: "dashboard-timeline-status--upcoming" };
  };

  const statusActive = statusForStep(
    activeStep,
    stepsWithDone.findIndex((s) => s.id === activeStep.id)
  );

  return (
    <div className="dashboard-card dashboard-timeline-card">
      <div className="dashboard-card-header">
        <h2>Process mot försäljning</h2>
        <div className="dashboard-timeline-header-meta">
          <span className="dashboard-tag">Steg: {doneCount}/{total} klara</span>
          {/* Avsluta-knappen syns bara när alla timeline-steg är klara
              och brokern har lifecycle-rätt. hideBilling → dialogen
              nämner inte Stripe här, bara att motparter sparkas ut. */}
          {doneCount === total && total > 0 && wsLifecycle?.can_manage_lifecycle && (
            <CloseWorkspaceButton
              workspace={wsLifecycle}
              onClosed={reloadLifecycle}
              label="Avsluta projekt"
              hideBilling
            />
          )}
          <span className="dashboard-chip">Linje: stannar vid första ofullständiga steg</span>
        </div>
      </div>

      <div className="dashboard-timeline-h">
        <div className="dashboard-timeline-track">
          <div className="dashboard-timeline-line-bg" />
          <div className="dashboard-timeline-line-progress" style={{ width: `${progressPct}%` }} />

          {stepsWithDone.map((step, index) => {
            const isDone = step.done;
            const isCurrent = index === currentIndex && !step.done;
            const isActive = step.id === activeStepId;
            const isTop = index % 2 === 0;
            const leftPct =
              stepsWithDone.length > 1
                ? (index / (stepsWithDone.length - 1)) * 100
                : 50;

            return (
              <button
                key={step.id}
                type="button"
                className={
                  "dashboard-timeline-node-wrapper" +
                  (isActive ? " dashboard-timeline-node-wrapper--active" : "") +
                  (isTop
                    ? " dashboard-timeline-node-wrapper--top"
                    : " dashboard-timeline-node-wrapper--bottom")
                }
                style={{ left: `${leftPct}%` }}
                onClick={() => setActiveStepId(step.id)}
              >
                <div
                  className={
                    "dashboard-timeline-node" +
                    (isDone
                      ? " dashboard-timeline-node--done"
                      : isCurrent
                      ? " dashboard-timeline-node--current"
                      : "")
                  }
                >
                  {isDone ? "✓" : step.stepNumber}
                </div>

                <div className="dashboard-timeline-node-label">
                  <div className="dashboard-timeline-node-step">Steg {step.stepNumber}</div>
                  <div className="dashboard-timeline-node-title">{step.title}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {activeStep && (
        <div className="dashboard-timeline-detail">
          <div className="dashboard-timeline-detail-header">
            <div>
              <div className="dashboard-timeline-detail-step">Steg {activeStep.stepNumber}</div>
              <div className="dashboard-timeline-detail-title">{activeStep.title}</div>
            </div>
            <span className={"dashboard-timeline-status " + statusActive.className}>
              {statusActive.label}
            </span>
          </div>

          <div className="dashboard-timeline-detail-main">
            <label className="dashboard-timeline-checkbox">
              <input
                type="checkbox"
                checked={activeStep.done}
                disabled={activeStep.autoDone}
                onChange={() => handleToggleStep(activeStep)}
              />
              <span>Markera som klart {activeStep.autoDone && "(styrt av systemdata)"}</span>
            </label>

            <button
              type="button"
              className="dashboard-timeline-detail-toggle"
              onClick={() => setShowRequirements((v) => !v)}
            >
              {showRequirements
                ? "Dölj vad som krävs innan steget är klart"
                : "Visa vad som krävs innan steget är klart"}
            </button>
          </div>

          {showRequirements && (
            <div className="dashboard-timeline-detail-body">
              <p>För att kunna stryka det här steget bör följande vara på plats:</p>
              <ul>
                {activeStep.details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================= Valuation stage banner ======================= */
function ValuationStageBanner({ completeness }) {
  const stages = [
    {
      key: "preliminary",
      label: "Preliminär",
      hint: "Baserat på historiska bokslut",
      done: !!completeness?.has_financials,
    },
    {
      key: "sie",
      label: "SIE-detaljerad",
      hint: "Efter uppladdad SIE-fil",
      done: !!completeness?.has_sie,
    },
    {
      key: "risk_adjusted",
      label: "Riskjusterad",
      hint: "Efter ägarformulär & riskprofil",
      done: !!completeness?.has_risk_profile && !!completeness?.has_owner_questionnaire,
    },
    {
      key: "dd_verified",
      label: "DD-verifierad",
      hint: "Efter due diligence-genomlysning",
      done: !!completeness?.has_ddq || !!completeness?.dd_in_progress,
    },
    {
      key: "final",
      label: "Slutgiltig",
      hint: "Klar för marknad",
      done: !!completeness?.ready_for_suggested_price,
    },
  ];

  // Current stage = first one that's not done, or the last one if all done
  const currentIdx = stages.findIndex((s) => !s.done);
  const activeIdx = currentIdx === -1 ? stages.length - 1 : Math.max(0, currentIdx - 1);

  return (
    <div
      style={{
        background: "#FEFCE8",
        border: "1px solid #FDE68A",
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 16,
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: "#78350F", lineHeight: 1.5 }}>
          <strong>Värderingen uppdateras i flera steg</strong> allteftersom mer data kommer in.
          Nuvarande värde är <strong>{stages[activeIdx].label.toLowerCase()}</strong> — det smalnar av
          när du fyller i fler delar av genomlysningen.
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {stages.map((stage, idx) => {
          const isDone = stage.done;
          const isActive = idx === activeIdx;
          return (
            <div
              key={stage.key}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
              }}
            >
              {idx > 0 && (
                <div
                  style={{
                    position: "absolute",
                    left: "-50%",
                    right: "50%",
                    top: 11,
                    height: 2,
                    background: isDone || stages[idx - 1].done ? "#92400e" : "#FDE68A",
                  }}
                />
              )}
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: isDone ? "#92400e" : isActive ? "#fff" : "#FEFCE8",
                  border: `2px solid ${isDone || isActive ? "#92400e" : "#FDE68A"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: isDone ? "#fff" : "#92400e",
                  fontWeight: 700,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {isDone ? "✓" : idx + 1}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 600,
                  color: "#78350F",
                  marginTop: 6,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {stage.label}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#92400e",
                  marginTop: 2,
                  textAlign: "center",
                  maxWidth: 110,
                  lineHeight: 1.3,
                }}
              >
                {stage.hint}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ======================= Valuation card ======================= */
function ValuationCard({ valuation, completeness }) {
  if (!valuation) {
    return (
      <div className="dashboard-card">
        <div className="dashboard-card-header">
          <h2>Värdering</h2>
        </div>
        <ValuationStageBanner completeness={completeness} />
        <p className="dashboard-muted">
          Ingen värdering beräknad ännu. Kör värderingsmotorn efter att bokslut har hämtats.
        </p>
      </div>
    );
  }

  const { baseline_span, ev_span, equity_span, currency, branch } = valuation;

  return (
    <div className="dashboard-card dashboard-card--primary">
      <div className="dashboard-card-header">
        <h2>Värdering</h2>
        <span className="dashboard-tag">
          {branch?.label || "Branschprofil"} • {valuation.as_of_date || "—"}
        </span>
      </div>

      <ValuationStageBanner completeness={completeness} />

      <div className="dashboard-kpi-row">
        <div className="dashboard-kpi">
          <span className="dashboard-kpi-label">Baseline EV (spann)</span>
          <span className="dashboard-kpi-value">
            {formatCurrency(baseline_span?.low, currency)} –{" "}
            {formatCurrency(baseline_span?.high, currency)}
          </span>
          <span className="dashboard-kpi-sub">
            Multipel:{" "}
            {valuation.pre_risk_multiple
              ? `${Number(valuation.pre_risk_multiple).toFixed(1)}x`
              : "—"}
          </span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi-label">Riskjusterad EV (spann)</span>
          <span className="dashboard-kpi-value">
            {formatCurrency(ev_span?.low, currency)} –{" "}
            {formatCurrency(ev_span?.high, currency)}
          </span>
          <span className="dashboard-kpi-sub">
            Riskfaktor:{" "}
            {valuation.risk_factor ? Number(valuation.risk_factor).toFixed(2) : "1.00"}
          </span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi-label">Equity value (spann)</span>
          <span className="dashboard-kpi-value">
            {formatCurrency(equity_span?.low, currency)} –{" "}
            {formatCurrency(equity_span?.high, currency)}
          </span>
          <span className="dashboard-kpi-sub">EV + nettokassa</span>
        </div>
      </div>

      <div className="dashboard-multipliers">
        <div>
          <span className="dashboard-muted">Bas-multipel</span>
          <strong>
            {valuation.base_multiple
              ? `${Number(valuation.base_multiple).toFixed(1)}x`
              : "—"}
          </strong>
        </div>
        <div>
          <span className="dashboard-muted">Storlek</span>
          <strong>{Number(valuation.size_factor).toFixed(2)}×</strong>
        </div>
        <div>
          <span className="dashboard-muted">Tillväxt</span>
          <strong>{Number(valuation.growth_factor).toFixed(2)}×</strong>
        </div>
        <div>
          <span className="dashboard-muted">Lönsamhet</span>
          <strong>{Number(valuation.profitability_factor).toFixed(2)}×</strong>
        </div>
        <div>
          <span className="dashboard-muted">Risk</span>
          <strong>{Number(valuation.risk_factor).toFixed(2)}×</strong>
        </div>
        <div>
          <span className="dashboard-muted">Slutlig multipel</span>
          <strong>
            {valuation.adjusted_multiple
              ? `${Number(valuation.adjusted_multiple).toFixed(1)}x`
              : "—"}
          </strong>
        </div>
      </div>
    </div>
  );
}

/* ======================= Value drivers helpers ======================= */

function normalizeDriverItem(item) {
  // Supports:
  // - "some text"
  // - { text: "...", evidence: { fields: [...], values: [...] } }
  if (!item) return { text: "", evidence: null };
  if (typeof item === "string") return { text: item.trim(), evidence: null };
  if (typeof item === "object") {
    const text = typeof item.text === "string" ? item.text.trim() : "";
    const evidence =
      item.evidence && typeof item.evidence === "object"
        ? {
            fields: Array.isArray(item.evidence.fields) ? item.evidence.fields : [],
            values: Array.isArray(item.evidence.values) ? item.evidence.values : [],
          }
        : null;
    return { text, evidence };
  }
  return { text: String(item).trim(), evidence: null };
}

function normalizeDriversList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map(normalizeDriverItem)
    .filter((x) => x.text)
    .slice(0, 7);
}

/* ======================= Value drivers / levers (AI-aware) ======================= */

function ValueLeversCard({
  valuation,
  risk,
  ownerQ,
  aiPayload,
  aiLoading,
  aiError,
  aiRefreshed,
  quality,
  coverage,
}) {
  const totalRisk = risk?.total_risk_score;
  const riskFactor =
    risk?.normalized_risk_factor != null ? Number(risk.normalized_risk_factor).toFixed(2) : null;

  const positiveItems = normalizeDriversList(aiPayload?.positive);
  const negativeItems = normalizeDriversList(aiPayload?.negative);
  const tinyNote = typeof aiPayload?.tiny_note === "string" ? aiPayload.tiny_note : "";

  const useAI = positiveItems.length > 0 || negativeItems.length > 0;

  const renderEvidence = (evidence) => {
    if (!evidence) return null;
    const values = (evidence.values || []).filter(Boolean).slice(0, 3);
    if (!values.length) return null;
    return <div className="dashboard-evidence">• {values.join(" • ")}</div>;
  };

  return (
    <div className="dashboard-card">
      <div className="dashboard-card-header">
        <h2>Vad påverkar värderingen?</h2>
        {totalRisk != null && (
          <span className="dashboard-tag">
            Samlad risk: {totalRisk} • faktor {riskFactor}×
          </span>
        )}
      </div>

      <PrecisionPanel quality={quality} coverage={coverage} />

      {/* Neat loading / error */}
      {aiLoading && (
        <div className="dashboard-ai-loading" aria-live="polite">
          <div style={{ display: "flex", gap: 6, color: "#334155" }}>
            <span className="dashboard-ai-dot" />
            <span className="dashboard-ai-dot" />
            <span className="dashboard-ai-dot" />
          </div>
          <div className="dashboard-ai-loading-text">
            Hämtar bolagsspecifika värdedrivare…
            <div className="dashboard-ai-loading-sub">Det här kan ta några sekunder.</div>
          </div>
        </div>
      )}

      {!aiLoading && aiError && <div className="dashboard-ai-error">{aiError}</div>}

      {!aiLoading && !aiError && aiRefreshed && (
        <p className="dashboard-muted dashboard-muted--tiny" style={{ marginTop: 8 }}>
          Uppdaterat med senaste underlag.
        </p>
      )}

      <div className="dashboard-two-col dashboard-two-col--stack-mobile">
        <div className="dashboard-value-box">
          <h3>Sådant som kan höja värderingen</h3>
          <ul className="dashboard-checklist-list">
            {useAI ? (
              positiveItems.map((it, i) => (
                <li key={`pos-${i}`}>
                  {it.text}
                  {renderEvidence(it.evidence)}
                </li>
              ))
            ) : (
              <>
                <li>Stark och förutsägbar omsättningstillväxt över flera år.</li>
                <li>Stabil lönsamhet och god kassagenerering.</li>
                <li>Diversifierad kundbas utan alltför stor kundkoncentration.</li>
                <li>Tydlig affärsmodell och skalbara processer.</li>
                <li>Välstrukturerade kund- och leverantörsavtal med rimlig riskfördelning.</li>
                <li>Begränsad nyckelpersonrisk och dokumenterad kunskap.</li>
                <li>God intern kontroll, rapportering och compliance-rutiner.</li>
              </>
            )}
          </ul>
        </div>

        <div className="dashboard-value-box dashboard-value-box--negative">
          <h3>Sådant som typiskt sänker värderingen</h3>
          <ul className="dashboard-checklist-list">
            {useAI ? (
              negativeItems.map((it, i) => (
                <li key={`neg-${i}`}>
                  {it.text}
                  {renderEvidence(it.evidence)}
                </li>
              ))
            ) : (
              <>
                <li>Hög beroendegrad av en eller ett fåtal nyckelpersoner.</li>
                <li>Stor kundkoncentration (enstaka kunder står för stor andel).</li>
                <li>Svag eller volatil lönsamhet, eller negativt resultat.</li>
                <li>Avsaknad av viktiga avtal (t.ex. IP, leverantörer, anställningar).</li>
                <li>Pågående eller sannolika tvister och juridiska risker.</li>
                <li>Bristande dokumentation inför due diligence.</li>
                <li>Regulatorisk osäkerhet eller compliance-brister.</li>
              </>
            )}
          </ul>
        </div>
      </div>

      {tinyNote ? <p className="dashboard-muted dashboard-muted--tiny">{tinyNote}</p> : null}

      {valuation && (
        <p className="dashboard-muted dashboard-muted--tiny">
          Tips: När du fyller i fler steg ovan blir analysen mer prissäker och mer “köpar-ready”.
        </p>
      )}
    </div>
  );
}

/* ======================= Financials: kombinerad ======================= */

function FinancialOverviewCard({ rawYears }) {
  // Default to 3 years — that's the window the valuation uses. User can
  // expand to 5 for a longer historical view.
  const [range, setRange] = React.useState(3);

  const sortedAll = React.useMemo(() => {
    if (!rawYears || !rawYears.length) return [];
    return [...rawYears].sort((a, b) =>
      String(a.fiscal_year_label).localeCompare(String(b.fiscal_year_label))
    );
  }, [rawYears]);

  const chartSource = React.useMemo(() => {
    if (!sortedAll.length) return [];
    // SIE LTM is a separate "year" appended by the backend; the range toggle
    // controls how many *bokslut* to show, then SIE is added on top.
    const bokslut = sortedAll.filter((y) => !y?.is_sie);
    const sieRows = sortedAll.filter((y) => y?.is_sie);
    const sliced = bokslut.slice(-range);
    const padCount = range - sliced.length;
    const padded = padCount > 0 ? [...Array(padCount).fill(null), ...sliced] : sliced;
    return [...padded, ...sieRows];
  }, [sortedAll, range]);

  const years = chartSource.map((y) => y?.fiscal_year_label || "");
  const revenue = chartSource.map((y) => (y ? y.revenue || null : null));
  const profit = chartSource.map((y) =>
    y ? y.result_after_financial_items || null : null
  );
  // Bokslut-table should show the latest bokslut, not SIE (SIE has no balance data).
  const latestYear =
    [...chartSource].reverse().find((y) => y && !y.is_sie) ||
    chartSource[chartSource.length - 1] ||
    null;
  const hasData = chartSource.some((y) => y);
  const realCount = chartSource.filter(Boolean).length;

  return (
    <div className="dashboard-card">
      <div className="dashboard-card-header">
        <h2>Bokslut och nyckeltal</h2>
        <div className="dashboard-range-toggle" role="tablist" aria-label="Tidsspann">
          <button
            type="button"
            role="tab"
            aria-selected={range === 3}
            className={
              "dashboard-range-btn" + (range === 3 ? " dashboard-range-btn--active" : "")
            }
            onClick={() => setRange(3)}
          >
            3 år
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={range === 5}
            className={
              "dashboard-range-btn" + (range === 5 ? " dashboard-range-btn--active" : "")
            }
            onClick={() => setRange(5)}
          >
            5 år
          </button>
        </div>
      </div>

      {!hasData && <p className="dashboard-muted">Inga årsredovisningar har analyserats ännu.</p>}

      {hasData && (
        <>
          <div className="dashboard-financials-grid">
            <div className="dashboard-chart-wrapper">
              <RevenueProfitChart years={years} revenue={revenue} profitAfterFin={profit} />
            </div>
            <BokslutTable latestYear={latestYear} years={years} revenue={revenue} profit={profit} />
          </div>

          <YoYRow years={years} revenue={revenue} profit={profit} />

          {range === 3 && (
            <p className="dashboard-muted dashboard-muted--tiny" style={{ marginTop: 12 }}>
              Värderingen baseras på de senaste {Math.min(3, realCount)} räkenskapsåren.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ======================= Bokslut table (latest year + averages) ======================= */

function BokslutTable({ latestYear, years, revenue, profit }) {
  const fmt = (n) =>
    n == null || !isFinite(n)
      ? "—"
      : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(n));

  const fmtPct = (x) => (isFinite(x) ? `${(x * 100).toFixed(1)}%` : "—");

  const label = latestYear?.fiscal_year_label || "—";
  const omsattning = latestYear?.revenue ?? null;
  const aretsResultat = latestYear?.result_after_financial_items ?? null;
  const sumTillgangar = latestYear?.total_assets ?? null;
  const egetKapital = latestYear?.total_equity ?? null;

  const revClean = (revenue || []).filter((v) => v != null);
  const profClean = (profit || []).filter((v) => v != null);
  const revCagrVal = cagr(revClean);
  const profAvgVal = avgYoY(profClean);

  // Latest YoY = the growth rate the valuation engine actually uses.
  const yoyRev = yoyArray(revenue);
  const yoyProf = yoyArray(profit);
  const lastRevYoY = [...yoyRev].reverse().find((v) => v != null) ?? null;
  const lastProfYoY = [...yoyProf].reverse().find((v) => v != null) ?? null;

  const rows = [
    { label: "Omsättning", value: fmt(omsattning), highlight: true },
    { label: "Årets resultat", value: fmt(aretsResultat), highlight: true },
    { label: "Summa tillgångar", value: fmt(sumTillgangar) },
    { label: "Eget kapital", value: fmt(egetKapital) },
    {
      label: "Tillväxt senaste året",
      value: fmtPct(lastRevYoY),
      title:
        "Omsättningstillväxt från föregående år till det senaste. Det är värdet värderingsmotorn använder.",
    },
    {
      label: "Resultatförändring senaste året",
      value: fmtPct(lastProfYoY),
      title: "Förändring i resultat efter finansnetto från föregående år.",
    },
    {
      label: "Snitt-CAGR omsättning",
      value: fmtPct(revCagrVal),
      title:
        "Compound Annual Growth Rate — den konstanta årliga tillväxt som skulle ta första årets omsättning till det sista. Drar ner vid volatila år.",
    },
    {
      label: "Snitt YoY resultat",
      value: fmtPct(profAvgVal),
      title: "Aritmetiskt snitt av år-för-år-förändring i resultat.",
    },
  ];

  return (
    <div className="dashboard-bokslut">
      <div className="dashboard-bokslut-header">
        <span className="dashboard-bokslut-title">Bokslut</span>
        <span className="dashboard-bokslut-year">{label}</span>
      </div>
      <div className="dashboard-bokslut-rows">
        {rows.map((r, i) => (
          <div
            key={r.label}
            className={
              "dashboard-bokslut-row" +
              (r.highlight ? " dashboard-bokslut-row--highlight" : "") +
              (i % 2 === 0 ? "" : " dashboard-bokslut-row--alt")
            }
            title={r.title || undefined}
          >
            <span className="dashboard-bokslut-row-label">{r.label}</span>
            <span className="dashboard-bokslut-row-value">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================= YoY pill row (kept separate from chips) ======================= */

function YoYRow({ years, revenue, profit }) {
  const fmtPct = (x) => (isFinite(x) ? `${(x * 100).toFixed(1)}%` : "—");
  const yoyRev = yoyArray(revenue);
  const yoyProf = yoyArray(profit);

  const items = years
    .map((year, i) => ({ year, rev: yoyRev[i], prof: yoyProf[i] }))
    .filter((it) => it.year && (it.rev != null || it.prof != null));

  if (!items.length) return null;

  return (
    <div className="dashboard-yoy-section">
      <h3 className="dashboard-yoy-title">Tillväxt år för år</h3>
      <p className="dashboard-yoy-subtitle">
        Procentuell förändring i omsättning och resultat från föregående år.
      </p>
      <div className="dashboard-yoy-row">
        {items.map((it) => (
          <div key={it.year} className="dashboard-yoy-pill">
            <div className="dashboard-yoy-year">{it.year}</div>
            <div className="dashboard-yoy-metric">Oms: {fmtPct(it.rev)}</div>
            <div className="dashboard-yoy-metric">Resultat: {fmtPct(it.prof)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================= IM-checklista ======================= */

function IMChecklistCard({ completeness, orgnr }) {
  // Stable keys decouple persistence from label edits.
  const imItems = [
    { key: "im_company_description", label: "Kort bolagsbeskrivning (historia, affärsmodell, positionering)." },
    { key: "im_products_markets", label: "Översikt av produkter/tjänster och målmarknader." },
    { key: "im_organization", label: "Beskrivning av organisation och nyckelpersoner." },
    { key: "im_financial_history", label: "Sammanfattning av finansiell historik (3–5 år)." },
    { key: "im_customers", label: "Översikt över kundbas och viktigaste kundkontrakt." },
    { key: "im_strategy", label: "Strategi, tillväxtplaner och investeringar framåt." },
  ];

  const ddItems = [
    { key: "dd_corporate_formalities", label: "Bolagsformalia (bolagsordning, aktiebok, ägarstruktur)." },
    { key: "dd_financial_reports", label: "Årsredovisningar och månads-/kvartalsrapporter." },
    { key: "dd_customer_supplier_contracts", label: "Väsentliga kund- och leverantörsavtal." },
    { key: "dd_leases_financing", label: "Hyresavtal, finansieringsavtal och säkerheter." },
    { key: "dd_ip_licenses", label: "IP-rättigheter, licenser och eventuella registreringar." },
    { key: "dd_personnel", label: "Personalavtal, incitamentsprogram och policydokument." },
    { key: "dd_compliance", label: "Compliance-underlag (GDPR, branschspecifik reglering)." },
    { key: "dd_disputes_risks", label: "Översikt över tvister, claims och potentiella risker." },
  ];

  const [state, setState] = React.useState({});
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!orgnr) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/company/dashboard/${orgnr}/checklist/`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setState(data?.items || {});
      } catch (e) {
        console.error("checklist load failed", e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [orgnr]);

  const toggle = async (key) => {
    if (!orgnr) return;
    const next = !state[key];
    setState((prev) => ({ ...prev, [key]: next }));
    try {
      const res = await fetch(`${API_BASE}/company/dashboard/${orgnr}/checklist/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ key, done: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      console.error("checklist save failed", e);
      // revert optimistic update
      setState((prev) => ({ ...prev, [key]: !next }));
    }
  };

  const renderItem = (it) => {
    const done = !!state[it.key];
    return (
      <li
        key={it.key}
        className={"dashboard-checklist-item" + (done ? " dashboard-checklist-item--done" : "")}
        onClick={() => toggle(it.key)}
        style={{
          cursor: loaded ? "pointer" : "default",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "6px 0",
        }}
      >
        <input
          type="checkbox"
          checked={done}
          onChange={() => toggle(it.key)}
          onClick={(e) => e.stopPropagation()}
          disabled={!loaded}
          aria-label={it.label}
        />
        <span style={done ? { textDecoration: "line-through", opacity: 0.55 } : undefined}>
          {it.label}
        </span>
      </li>
    );
  };

  return (
    <div className="dashboard-card">
      <div className="dashboard-card-header">
        <h2>Checklista för IM &amp; Due Diligence</h2>
        <span className="dashboard-tag">
          IM-status: {completeness?.ready_for_suggested_price ? "nära klart" : "pågår"}
        </span>
      </div>

      <div className="dashboard-two-col dashboard-two-col--stack-mobile">
        <div className="dashboard-checklist-block">
          <h3>IM – innehåll som bör vara på plats</h3>
          <ul className="dashboard-checklist-list" style={{ listStyle: "none", paddingLeft: 0 }}>
            {imItems.map(renderItem)}
          </ul>
        </div>

        <div className="dashboard-checklist-block">
          <h3>Due diligence – underlag att samla</h3>
          <ul className="dashboard-checklist-list" style={{ listStyle: "none", paddingLeft: 0 }}>
            {ddItems.map(renderItem)}
          </ul>
        </div>
      </div>

      <p className="dashboard-muted dashboard-muted--tiny">
        När dessa delar är på plats går det snabbare att ta fram ett IM och genomföra en effektiv due
        diligence utan onödiga stopp.
      </p>
    </div>
  );
}

/* ======================= Chart + growth ======================= */

function RevenueProfitChart({ years, revenue, profitAfterFin }) {
  const [hoverIndex, setHoverIndex] = React.useState(null);

  const w = 640;
  const h = 220;
  const p = 30;
  const n = years.length;

  const safeRevenue = revenue.map((v) => (v == null ? 0 : v));
  const safeProfit = profitAfterFin.map((v) => (v == null ? 0 : v));

  const allVals = [...safeRevenue, ...safeProfit].filter((v) => v !== null && v !== undefined);

  const minVal = allVals.length ? Math.min(0, ...allVals) : 0;
  const maxVal = allVals.length ? Math.max(0, ...allVals) : 1;
  const span = maxVal - minVal || 1;

  const xSlot = n ? (w - 2 * p) / n : w - 2 * p;
  const bw = Math.max(12, xSlot * 0.45);

  const sx = (i) => p + i * xSlot + xSlot / 2;
  const sy = (v) => h - p - ((v - minVal) / span) * (h - 2 * p);

  const baselineY = sy(0);

  const trend = linearRegression(safeRevenue.map((y, i) => [i, y]));
  const trendPath = n
    ? `M ${sx(0)} ${sy(trendAt(trend, 0))} L ${sx(n - 1)} ${sy(trendAt(trend, n - 1))}`
    : "";

  const hoveredYear = hoverIndex != null ? years[hoverIndex] : null;
  const hoveredRev = hoverIndex != null ? revenue[hoverIndex] : null;
  const hoveredProf = hoverIndex != null ? profitAfterFin[hoverIndex] : null;

  const maxHoveredVal = Math.max(hoveredRev || 0, hoveredProf || 0);
  const rawTooltipX = hoverIndex != null ? sx(hoverIndex) : null;
  const rawTooltipY = hoverIndex != null ? sy(maxHoveredVal || 0) - 10 : null;

  const tooltipWidth = 180;
  const tooltipHeight = hoveredRev != null && hoveredProf != null ? 56 : 40;

  const tooltipX =
    rawTooltipX == null
      ? null
      : Math.min(Math.max(rawTooltipX, tooltipWidth / 2 + 16), w - tooltipWidth / 2 - 16);
  const tooltipY = rawTooltipY == null ? null : Math.max(tooltipHeight + 8, rawTooltipY);

  const fmtK = (n) =>
    new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="dashboard-bars"
      role="img"
      aria-label="Omsättning och resultat efter finansiella poster"
    >
      <line x1="0" y1={baselineY} x2={w} y2={baselineY} className="dashboard-gridline" />

      {revenue.map((v, i) => {
        if (v == null) return null;
        const val = Number(v) || 0;
        const x = sx(i) - bw - 3;
        const yVal = sy(val);

        const y = val >= 0 ? yVal : baselineY;
        const bh = val >= 0 ? baselineY - yVal : sy(val) - baselineY;

        return (
          <rect
            key={`rev-${i}`}
            x={x}
            y={y}
            width={bw}
            height={Math.max(0, bh)}
            className="dashboard-bar"
          />
        );
      })}

      {profitAfterFin.map((v, i) => {
        if (v == null) return null;
        const val = Number(v) || 0;
        const x = sx(i) + 3;
        const yVal = sy(val);

        const y = val >= 0 ? yVal : baselineY;
        const bh = val >= 0 ? baselineY - yVal : sy(val) - baselineY;

        return (
          <rect
            key={`paf-${i}`}
            x={x}
            y={y}
            width={bw}
            height={Math.max(0, bh)}
            className="dashboard-bar--light"
          />
        );
      })}

      {trendPath && <path d={trendPath} className="dashboard-line-path" />}

      {years.map((y, i) => (
        <text
          key={`xl-${i}`}
          x={sx(i)}
          y={h - 8}
          textAnchor="middle"
          className="dashboard-xlabel"
        >
          {y}
        </text>
      ))}

      {years.map((label, i) => {
        if (!label) return null;
        return (
          <rect
            key={`hit-${i}`}
            x={sx(i) - xSlot / 2}
            y={0}
            width={xSlot}
            height={h}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(i)}
            onMouseLeave={() => setHoverIndex(null)}
          />
        );
      })}

      {hoverIndex != null &&
        hoveredYear &&
        (hoveredRev != null || hoveredProf != null) &&
        tooltipX != null &&
        tooltipY != null && (
          <g
            className="dashboard-tooltip"
            transform={`translate(${tooltipX - tooltipWidth / 2}, ${tooltipY - tooltipHeight - 8})`}
          >
            <rect
              x={0}
              y={0}
              width={tooltipWidth}
              height={tooltipHeight}
              rx={8}
              ry={8}
              fill="#ffffff"
              stroke="#e5e7eb"
              strokeWidth="1"
              style={{ filter: "drop-shadow(0 10px 25px rgba(15,23,42,0.15))" }}
            />
            <text
              x={tooltipWidth / 2}
              y={16}
              textAnchor="middle"
              style={{ fontSize: 11, fontWeight: 600, fill: "#111827" }}
            >
              {hoveredYear}
            </text>
            {hoveredRev != null && (
              <text
                x={tooltipWidth / 2}
                y={hoveredProf != null ? 30 : 32}
                textAnchor="middle"
                style={{ fontSize: 11, fill: "#111827" }}
              >
                Omsättning: {fmtK(hoveredRev)} SEK
              </text>
            )}
            {hoveredProf != null && (
              <text
                x={tooltipWidth / 2}
                y={44}
                textAnchor="middle"
                style={{ fontSize: 11, fill: "#111827" }}
              >
                Resultat: {fmtK(hoveredProf)} SEK
              </text>
            )}
          </g>
        )}
    </svg>
  );
}

/* ======================= tiny math helpers ======================= */

function linearRegression(points) {
  const n = points.length || 1;
  const sx = points.reduce((s, p) => s + p[0], 0);
  const sy = points.reduce((s, p) => s + p[1], 0);
  const sxx = points.reduce((s, p) => s + p[0] * p[0], 0);
  const sxy = points.reduce((s, p) => s + p[0] * p[1], 0);
  const denom = n * sxx - sx * sx || 1;
  const m = (n * sxy - sx * sy) / denom;
  const b = (sy - m * sx) / n;
  return { m, b };
}

function trendAt({ m, b }, x) {
  return m * x + b;
}

function cagr(arr) {
  const n = arr?.length ?? 0;
  if (n < 2) return NaN;
  const first = arr[0] || 0;
  const last = arr[n - 1] || 0;
  if (first <= 0 || last <= 0)
    return (last - first) / Math.max(1, n - 1) / Math.max(1, first);
  return Math.pow(last / first, 1 / (n - 1)) - 1;
}

function avgYoY(arr) {
  const n = arr?.length ?? 0;
  if (n < 2) return NaN;
  let acc = 0;
  let cnt = 0;
  for (let i = 1; i < n; i++) {
    const prev = arr[i - 1] || 0;
    const cur = arr[i] || 0;
    if (prev === 0) continue;
    acc += (cur - prev) / Math.abs(prev || 1);
    cnt++;
  }
  return cnt ? acc / cnt : NaN;
}

function yoyArray(arr) {
  const n = arr?.length ?? 0;
  if (n < 2) return Array(n).fill(null);
  const out = Array(n).fill(null);
  for (let i = 1; i < n; i++) {
    const prev = arr[i - 1];
    const cur = arr[i];
    if (prev == null || cur == null || prev === 0) continue;
    out[i] = (cur - prev) / Math.abs(prev);
  }
  return out;
}

// "Nästa steg"-card. Backend computes the single highest-priority action
// for this workspace based on deal_stage + completion of seller-submission,
// DD, financials and valuation. Card stays out of broker's face on closed
// or fully-set-up workspaces by returning next_action=null.
function NextActionCard({ data }) {
  const a = data?.next_action;
  if (!a) return null;
  const isHigh = a.priority === "high";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "16px 20px",
        marginBottom: 20,
        background: isHigh ? "#FFFBEB" : "#F0F9FF",
        border: `1px solid ${isHigh ? "#FCD34D" : "#BAE6FD"}`,
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{
        flexShrink: 0,
        width: 40, height: 40,
        borderRadius: 10,
        background: isHigh ? "#F59E0B" : "#0EA5E9",
        color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 20,
      }}>
        {isHigh ? "⚠" : "→"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
                       textTransform: "uppercase", color: "#6b7280", marginBottom: 4 }}>
          Nästa steg · {data.deal_stage_label || data.deal_stage}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1917", marginBottom: 4 }}>
          {a.title}
        </div>
        <div style={{ fontSize: 13, color: "#57534e", lineHeight: 1.4 }}>
          {a.description}
        </div>
      </div>
      <a
        href={a.cta_to}
        style={{
          flexShrink: 0,
          padding: "10px 18px",
          fontSize: 14,
          fontWeight: 600,
          background: isHigh ? "#1c1917" : "#0369a1",
          color: "#fff",
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        {a.cta_label}
      </a>
    </div>
  );
}
